import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users as UsersIcon, 
  Trash2, 
  Shield,
  User,
  Calendar,
  Plus,
  Edit,
  Key,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Users() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [roleAssign, setRoleAssign] = useState({ userId: null, roleId: '' });

  // Roles state
  const [permissions, setPermissions] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: []
  });
  
  // User permissions dialog state
  const [userPermDialogOpen, setUserPermDialogOpen] = useState(false);
  const [editingUserPerms, setEditingUserPerms] = useState(null);
  const [userPermForm, setUserPermForm] = useState({
    permissions: [],
    useCustom: false
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/roles/permissions`);
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/users/${deleteId}`);
      toast.success('Usuario eliminado');
      setDeleteId(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleAssignRole = async () => {
    if (!roleAssign.userId || !roleAssign.roleId) return;
    try {
      await axios.put(`${API}/users/${roleAssign.userId}/assign-role?role_id=${roleAssign.roleId}`);
      toast.success('Rol asignado correctamente');
      setRoleAssign({ userId: null, roleId: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar rol');
    }
  };

  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions || []
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        name: '',
        description: '',
        permissions: []
      });
    }
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      toast.error('El nombre del rol es obligatorio');
      return;
    }

    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, roleForm);
        toast.success('Rol actualizado');
      } else {
        await axios.post(`${API}/roles`, roleForm);
        toast.success('Rol creado');
      }
      setRoleDialogOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el rol');
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteRoleId) return;
    try {
      await axios.delete(`${API}/roles/${deleteRoleId}`);
      toast.success('Rol eliminado');
      setDeleteRoleId(null);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el rol');
    }
  };

  const togglePermission = (permission) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const toggleCategoryPermissions = (categoryPerms) => {
    const allSelected = categoryPerms.every(p => roleForm.permissions.includes(p));
    setRoleForm(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  // User permissions functions
  const openUserPermissionsDialog = async (user) => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/permissions`);
      const data = response.data;
      setEditingUserPerms({
        ...user,
        rolePermissions: data.role_permissions,
        customPermissions: data.custom_permissions,
        effectivePermissions: data.effective_permissions
      });
      setUserPermForm({
        permissions: data.effective_permissions || [],
        useCustom: data.has_custom_permissions
      });
      setUserPermDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar permisos del usuario');
    }
  };

  const handleSaveUserPermissions = async () => {
    if (!editingUserPerms) return;
    try {
      if (userPermForm.useCustom) {
        await axios.put(`${API}/users/${editingUserPerms.id}/permissions`, {
          permissions: userPermForm.permissions
        });
        toast.success('Permisos personalizados guardados');
      } else {
        // Reset to role permissions by setting custom_permissions to null
        await axios.put(`${API}/users/${editingUserPerms.id}/permissions`, {
          permissions: null
        });
        toast.success('Permisos restaurados desde el rol');
      }
      setUserPermDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar permisos');
    }
  };

  const toggleUserPermission = (permission) => {
    setUserPermForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const toggleUserCategoryPermissions = (categoryPerms) => {
    const allSelected = categoryPerms.every(p => userPermForm.permissions.includes(p));
    setUserPermForm(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPerms.includes(p))
        : [...new Set([...prev.permissions, ...categoryPerms])]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const getRoleBadge = (user) => {
    if (user.role === 'super_admin') {
      return (
        <Badge className="bg-violet-100 text-violet-800 border-violet-300 border">
          <Shield size={12} className="mr-1" />
          Super Admin
        </Badge>
      );
    }
    const roleName = user.role_name || (user.role === 'admin' ? 'Admin' : 'Mecánico');
    const isAdmin = user.role === 'admin';
    
    return (
      <Badge className={isAdmin ? "bg-orange-100 text-orange-800 border-orange-200" : "bg-slate-50 border-slate-200"}>
        {isAdmin ? <Shield size={12} className="mr-1" /> : <User size={12} className="mr-1" />}
        {roleName}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="users-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900">
          Usuarios y Roles
        </h1>
        <p className="text-slate-500 mt-1">Gestiona usuarios y sus permisos</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'users'
              ? 'text-orange-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-users"
        >
          <span className="flex items-center gap-2">
            <UsersIcon size={16} />
            Usuarios
          </span>
          {activeTab === 'users' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'roles'
              ? 'text-orange-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-roles"
        >
          <span className="flex items-center gap-2">
            <Key size={16} />
            Roles
          </span>
          {activeTab === 'roles' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
          )}
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <UsersIcon size={18} className="text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold">Total Usuarios</p>
                    <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">{users.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Shield size={18} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold">Administradores</p>
                    <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">
                      {users.filter(u => u.role === 'admin').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Key size={18} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold">Roles Disponibles</p>
                    <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">{roles.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users table */}
          <Card className="border-slate-200" data-testid="users-table">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="spinner w-8 h-8" />
                </div>
              ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <UsersIcon size={48} className="mb-4" />
                  <p>No hay usuarios registrados</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Nombre</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Email</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Rol</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Registro</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                <span className="text-sm font-semibold text-slate-600">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <span className="font-medium text-slate-900">{user.name}</span>
                              {user.id === currentUser?.id && (
                                <Badge variant="outline" className="text-xs">Tú</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600">{user.email}</TableCell>
                          <TableCell>{getRoleBadge(user)}</TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(user.created_at)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {user.id !== currentUser?.id && (isSuperAdmin || user.role !== 'super_admin') && (
                              <div className="flex items-center justify-end gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-purple-500 hover:text-purple-600 hover:bg-purple-50"
                                  onClick={() => openUserPermissionsDialog(user)}
                                  title="Gestionar permisos"
                                  data-testid={`perms-user-${user.id}`}
                                >
                                  <Lock size={14} />
                                </Button>
                                <Select 
                                  value={user.role === 'super_admin' ? '__super_admin__' : (user.role_id || '')}
                                  onValueChange={(value) => setRoleAssign({ userId: user.id, roleId: value })}
                                >
                                  <SelectTrigger className="w-36" data-testid={`role-select-${user.id}`}>
                                    <SelectValue placeholder="Asignar rol" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {isSuperAdmin && (
                                      <SelectItem value="__super_admin__">
                                        <span className="flex items-center gap-1">
                                          <Shield size={12} className="text-violet-600" />
                                          Super Admin
                                        </span>
                                      </SelectItem>
                                    )}
                                    {roles.map(role => (
                                      <SelectItem key={role.id} value={role.id}>
                                        {role.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => setDeleteId(user.id)}
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <>
          <div className="flex justify-between items-center">
            <p className="text-slate-600">
              Define los roles y permisos para los usuarios de la aplicación
            </p>
            <Button 
              onClick={() => openRoleDialog()} 
              className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
              data-testid="create-role-btn"
            >
              <Plus size={16} />
              Nuevo Rol
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map(role => (
              <Card 
                key={role.id} 
                className={`border-slate-200 ${role.is_system ? 'bg-slate-50' : ''}`}
                data-testid={`role-card-${role.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                        {role.name === 'Admin' ? (
                          <Shield size={18} className="text-orange-500" />
                        ) : (
                          <Key size={18} className="text-slate-500" />
                        )}
                        {role.name}
                      </CardTitle>
                      {role.is_system && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          <Lock size={10} className="mr-1" />
                          Sistema
                        </Badge>
                      )}
                    </div>
                    {!role.is_system || role.name !== 'Admin' ? (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRoleDialog(role)}
                          data-testid={`edit-role-${role.id}`}
                        >
                          <Edit size={14} />
                        </Button>
                        {!role.is_system && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => setDeleteRoleId(role.id)}
                            data-testid={`delete-role-${role.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-3">
                    {role.description || 'Sin descripción'}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Permisos:</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      {role.name === 'Admin' ? 'Todos' : role.permissions?.length || 0}
                    </Badge>
                  </div>
                  <div className="mt-2 text-xs text-slate-400">
                    {users.filter(u => u.role_id === role.id || 
                      (!u.role_id && ((u.role === 'admin' && role.name === 'Admin') || 
                                      (u.role === 'mechanic' && role.name === 'Mecánico')))
                    ).length} usuario(s)
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">
              {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre del Rol *</Label>
                <Input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  placeholder="Ej: Supervisor, Técnico..."
                  disabled={editingRole?.is_system && editingRole?.name === 'Admin'}
                  data-testid="role-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  placeholder="Breve descripción del rol"
                  data-testid="role-description-input"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Permisos</Label>
              <p className="text-sm text-slate-500 mb-4">
                Selecciona los permisos que tendrá este rol
              </p>

              {permissions?.categories && Object.entries(permissions.categories).map(([category, perms]) => (
                <div key={category} className="mb-4">
                  <div 
                    className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-slate-50 p-2 rounded-lg -mx-2"
                    onClick={() => toggleCategoryPermissions(perms)}
                  >
                    <Checkbox
                      checked={perms.every(p => roleForm.permissions.includes(p))}
                      className="data-[state=checked]:bg-orange-500"
                    />
                    <span className="font-semibold text-slate-700">{category}</span>
                    <span className="text-xs text-slate-400">
                      ({roleForm.permissions.filter(p => perms.includes(p)).length}/{perms.length})
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-6">
                    {perms.map(perm => (
                      <div 
                        key={perm}
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded -mx-2"
                        onClick={() => togglePermission(perm)}
                      >
                        <Checkbox
                          checked={roleForm.permissions.includes(perm)}
                          className="data-[state=checked]:bg-orange-500"
                        />
                        <span className="text-sm text-slate-600">
                          {permissions.descriptions[perm] || perm}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-slate-500">
                {roleForm.permissions.length} permisos seleccionados
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveRole}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-role-btn"
                >
                  {editingRole ? 'Guardar Cambios' : 'Crear Rol'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role assign confirmation */}
      <Dialog open={!!roleAssign.userId} onOpenChange={() => setRoleAssign({ userId: null, roleId: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Asignar Rol</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que quieres asignar el rol <strong>{roles.find(r => r.id === roleAssign.roleId)?.name}</strong> a este usuario?
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRoleAssign({ userId: null, roleId: '' })}>
              Cancelar
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleAssignRole}
              data-testid="confirm-role-assign-btn"
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Eliminar Usuario</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que quieres eliminar este usuario? Esta acción no se puede deshacer.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteUser}
              data-testid="confirm-delete-user-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete role confirmation */}
      <Dialog open={!!deleteRoleId} onOpenChange={() => setDeleteRoleId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Eliminar Rol</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que quieres eliminar este rol? Asegúrate de que ningún usuario lo tenga asignado.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteRoleId(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteRole}
              data-testid="confirm-delete-role-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Permissions Dialog */}
      <Dialog open={userPermDialogOpen} onOpenChange={() => setUserPermDialogOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-permissions-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
              <Lock className="text-purple-500" />
              Permisos de {editingUserPerms?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editingUserPerms && permissions && (
            <div className="space-y-4">
              {/* Role info */}
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Rol actual:</strong>{' '}
                  <Badge variant="outline" className="ml-2">
                    {editingUserPerms.role_name || 'Sin rol asignado'}
                  </Badge>
                </p>
              </div>
              
              {/* Custom permissions toggle */}
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Checkbox
                  id="use-custom-perms"
                  checked={userPermForm.useCustom}
                  onCheckedChange={(checked) => {
                    setUserPermForm(prev => ({
                      ...prev,
                      useCustom: checked,
                      permissions: checked ? prev.permissions : (editingUserPerms.rolePermissions || [])
                    }));
                  }}
                  data-testid="use-custom-perms-checkbox"
                />
                <div>
                  <Label htmlFor="use-custom-perms" className="font-semibold text-purple-800">
                    Usar permisos personalizados
                  </Label>
                  <p className="text-xs text-purple-600">
                    Activa para personalizar los permisos de este usuario independientemente de su rol
                  </p>
                </div>
              </div>
              
              {/* Permissions grid */}
              <div className="space-y-4">
                {Object.entries(permissions.categories || {}).map(([category, categoryPerms]) => {
                  const selectedInCategory = categoryPerms.filter(p => userPermForm.permissions.includes(p));
                  const allSelected = selectedInCategory.length === categoryPerms.length;
                  const someSelected = selectedInCategory.length > 0 && !allSelected;
                  
                  return (
                    <div key={category} className="border rounded-lg overflow-hidden">
                      {/* Category header */}
                      <div 
                        className={`px-3 py-2 flex items-center justify-between cursor-pointer ${
                          userPermForm.useCustom ? 'bg-slate-100 hover:bg-slate-200' : 'bg-slate-50'
                        }`}
                        onClick={() => userPermForm.useCustom && toggleUserCategoryPermissions(categoryPerms)}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={allSelected}
                            ref={(el) => el && (el.indeterminate = someSelected)}
                            disabled={!userPermForm.useCustom}
                            onCheckedChange={() => toggleUserCategoryPermissions(categoryPerms)}
                          />
                          <span className="font-semibold text-sm text-slate-700">{category}</span>
                          <Badge variant="secondary" className="text-xs">
                            {selectedInCategory.length}/{categoryPerms.length}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Category permissions */}
                      <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {categoryPerms.map(perm => (
                          <div
                            key={perm}
                            className={`flex items-center gap-2 p-2 rounded ${
                              userPermForm.permissions.includes(perm) 
                                ? 'bg-green-50' 
                                : 'bg-slate-50'
                            } ${!userPermForm.useCustom ? 'opacity-50' : ''}`}
                          >
                            <Checkbox
                              id={`user-perm-${perm}`}
                              checked={userPermForm.permissions.includes(perm)}
                              onCheckedChange={() => toggleUserPermission(perm)}
                              disabled={!userPermForm.useCustom}
                            />
                            <Label 
                              htmlFor={`user-perm-${perm}`}
                              className={`text-xs cursor-pointer flex-1 ${
                                userPermForm.useCustom ? '' : 'cursor-not-allowed'
                              }`}
                            >
                              {permissions.descriptions?.[perm] || perm}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Summary */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Permisos activos:</strong> {userPermForm.permissions.length} de {permissions.all_permissions?.length || 0}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={() => setUserPermDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleSaveUserPermissions}
              data-testid="save-user-perms-btn"
            >
              Guardar Permisos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
